import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes, signOut } from 'aws-amplify/auth';
import { Container, User, Settings, LogOut, CreditCard, FileText, Download, Calendar, DollarSign } from 'lucide-react';
import AWS from 'aws-sdk';

// Configure AWS
AWS.config.update({
  region: process.env.REACT_APP_AWS_REGION,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
});

const s3 = new AWS.S3();
const dynamoDb = new AWS.DynamoDB.DocumentClient();
const cognitoIdp = new AWS.CognitoIdentityServiceProvider();

// Logo Component
function StoreLogo() {
  return (
    <div className="flex items-center gap-3">
      <img src="/Logo.png" alt="StoreHere" className="h-10 w-auto" />
      <div>
        <span className="text-xl font-bold text-gray-800">store</span>
        <span className="text-xl font-bold text-orange-500">here</span>
        <div className="text-xs text-gray-500 uppercase tracking-wider">SELF STORAGE</div>
      </div>
    </div>
  );
}

// Billing Component
function BillingTab({ userEmail, userId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextPayment, setNextPayment] = useState('--');
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState('');
  const [billingFrequency, setBillingFrequency] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateMsg, setUpdateMsg] = useState('');

  useEffect(() => {
    fetchBillingData();
    fetchSubscription();
  }, [userEmail]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      // Skip API call for now, go straight to S3
      await fetchInvoicesFromS3();
      await fetchPaymentInfo();
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      setSubLoading(true);
      setSubError('');
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString?.();
      if (!idToken) throw new Error('Missing auth token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/customer-subscription`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      const data = await res.json();
      const freq = data?.subscription?.billingFrequency || '';
      setBillingFrequency(freq);
      const nextEpoch = data?.subscription?.nextChargeAt || data?.subscription?.currentPeriodEnd;
      if (nextEpoch) {
        const d = new Date((Number(nextEpoch) || 0) * 1000);
        if (!isNaN(d.getTime())) setNextPayment(d.toLocaleDateString());
      }
    } catch (e) {
      console.error('Subscription load error:', e);
      setSubError('Failed to load subscription');
    } finally {
      setSubLoading(false);
    }
  };

  const updateFrequency = async (freq) => {
    try {
      setUpdating(true);
      setUpdateMsg('');
      setSubError('');
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString?.();
      if (!idToken) throw new Error('Missing auth token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/customer-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ billingFrequency: freq })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      setBillingFrequency(freq);
      setUpdateMsg('Billing frequency updated');
      // Refresh sub info and next payment date
      await fetchSubscription();
    } catch (e) {
      console.error('Update frequency error:', e);
      setSubError('Failed to update billing frequency');
    } finally {
      setUpdating(false);
      setTimeout(() => setUpdateMsg(''), 3000);
    }
  };

  const fetchInvoicesFromS3 = async () => {
    try {
      console.log('Looking for invoices with userId:', userId, 'userEmail:', userEmail);
      
      // Step 1: Find user's container in DynamoDB to get Stripe customer ID
      const stripeCustomerId = await findUserStripeCustomerId(userEmail);
      
      if (!stripeCustomerId) {
        console.log('No Stripe customer ID found for user');
        setInvoices([]);
        return;
      }
      
      console.log('Found Stripe customer ID:', stripeCustomerId);
      
      // Step 2: List invoices in S3 for this customer
      const params = {
        Bucket: 'storehere-invoices',
        Prefix: `invoices/${stripeCustomerId}/`,
      };
      
      const result = await s3.listObjectsV2(params).promise();
      const userInvoices = result.Contents || [];
      
      setInvoices(userInvoices.map(obj => ({
        key: obj.Key,
        date: obj.LastModified,
        size: obj.Size,
        name: obj.Key.split('/').pop(),
        stripeCustomerId: stripeCustomerId
      })));
    } catch (error) {
      console.error('Error fetching from S3:', error);
      setError('Failed to load invoices from storage');
    }
  };

  // Fetch payment info from DynamoDB
  const fetchPaymentInfo = async () => {
    try {
      const params = {
        TableName: 'storage-management',
        FilterExpression: 'customerEmail = :email',
        ExpressionAttributeValues: {
          ':email': userEmail
        }
      };
      
      const result = await dynamoDb.scan(params).promise();
      
      if (result.Items && result.Items.length > 0) {
        const container = result.Items[0];
        if (container.nextDueDate) {
          setNextPayment(new Date(container.nextDueDate).toLocaleDateString());
        }
      }
    } catch (error) {
      console.error('Error fetching payment info:', error);
    }
  };

  // Find user's Stripe customer ID from DynamoDB
  const findUserStripeCustomerId = async (email) => {
    try {
      const params = {
        TableName: 'storage-management',
        FilterExpression: 'customerEmail = :email',
        ExpressionAttributeValues: {
          ':email': email
        }
      };
      
      const result = await dynamoDb.scan(params).promise();
      const items = result.Items || [];
      if (items.length > 0) {
        // Prefer the most recently updated container for this customer
        items.sort((a, b) => {
          const da = new Date(a.lastPaymentDate || a.updatedAt || a.createdAt || 0).getTime();
          const db = new Date(b.lastPaymentDate || b.updatedAt || b.createdAt || 0).getTime();
          return db - da;
        });
        return items[0].stripeCustomerId;
      }
      
      return null;
    } catch (error) {
      console.error('Error finding Stripe customer ID:', error);
      return null;
    }
  };

  const downloadInvoice = async (key) => {
    try {
      const params = {
        Bucket: 'storehere-invoices',
        Key: key
      };
      
      const signedUrl = await s3.getSignedUrlPromise('getObject', {
        ...params,
        Expires: 3600 // 1 hour
      });
      
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading billing information...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Billing & Payments</h1>
      
      {/* Payment Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Next Payment</p>
              <p className="text-2xl font-bold text-gray-900">{nextPayment}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Billing Frequency</p>
              <p className="text-2xl font-bold text-gray-900">
                {subLoading ? 'Loading…' : (billingFrequency ? billingFrequency : '—')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Change Billing Frequency */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Change Billing Frequency</h3>
        {subError && <div className="mb-4 text-sm text-red-600">{subError}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          {['weekly','fortnightly','monthly'].map((opt) => (
            <label key={opt} className={`cursor-pointer border rounded-lg p-4 ${billingFrequency === opt ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
              <input
                type="radio"
                name="billingFrequency"
                className="sr-only"
                checked={billingFrequency === opt}
                onChange={() => setBillingFrequency(opt)}
              />
              <div className="text-center font-semibold capitalize">{opt}</div>
            </label>
          ))}
        </div>
        <button
          onClick={() => updateFrequency(billingFrequency)}
          disabled={updating || !billingFrequency}
          className="inline-flex items-center px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
        >
          {updating ? 'Updating…' : 'Save Changes'}
        </button>
        {updateMsg && <span className="ml-3 text-sm text-green-600">{updateMsg}</span>}
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Invoice History</h3>
        </div>
        <div className="overflow-x-auto">
          {invoices.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-[12rem] truncate">
                      {invoice.name || `Invoice ${index + 1}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(invoice.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Paid
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => downloadInvoice(invoice.key)}
                        className="text-orange-600 hover:text-orange-900 flex items-center gap-1"
                      >
                        <Download size={16} />
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-6 py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices</h3>
              <p className="mt-1 text-sm text-gray-500">No billing history available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Documents Component
function DocumentsTab({ userId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, [userId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      
      // List objects with user ID prefix to find timestamped files
      const params = {
        Bucket: 'storehere-agreements',
        Prefix: `agreements/${userId}`
      };
      
      const result = await s3.listObjectsV2(params).promise();
      
      if (result.Contents && result.Contents.length > 0) {
        const docs = result.Contents.map(obj => ({
          name: 'Storage Agreement',
          type: 'PDF',
          key: obj.Key,
          description: 'Signed storage container agreement',
          date: obj.LastModified
        }));
        setDocuments(docs);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (key) => {
    try {
      const params = {
        Bucket: 'storehere-agreements',
        Key: key
      };
      
      const signedUrl = await s3.getSignedUrlPromise('getObject', {
        ...params,
        Expires: 3600 // 1 hour
      });
      
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading documents...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Documents</h1>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Your Documents</h3>
          <p className="mt-1 text-sm text-gray-500">
            Download copies of your agreements and important documents.
          </p>
        </div>
        
        <div className="p-6">
          {documents.length > 0 ? (
            <div className="space-y-4">
              {documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="h-8 w-8 text-red-600" />
                    <div className="ml-4">
                      <h4 className="text-sm font-medium text-gray-900">{doc.name}</h4>
                      <p className="text-sm text-gray-500">{doc.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadDocument(doc.key)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No documents</h3>
              <p className="mt-1 text-sm text-gray-500">
                No documents available yet. Your signed agreements will appear here.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientManagement() {
  const [user, setUser] = useState(null);
  const [userAttributes, setUserAttributes] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [siteMapUrl, setSiteMapUrl] = useState(null);
  const [siteMapLoading, setSiteMapLoading] = useState(false);
  const [containerInfo, setContainerInfo] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const [memberSince, setMemberSince] = useState('');

  useEffect(() => {
    checkAuthState();
  }, []);

  useEffect(() => {
    if (userAttributes.email) {
      fetchContainerInfo();
    }
  }, [userAttributes.email]);

  useEffect(() => {
    // Load Cognito join date (AdminGetUser) when we have user + pool id
    const loadJoin = async () => {
      try {
        const poolId = process.env.REACT_APP_USER_POOL_ID;
        const username = (user && (user.username || user.userId)) || userAttributes.email;
        if (!poolId || !username) return;
        const resp = await cognitoIdp.adminGetUser({ UserPoolId: poolId, Username: username }).promise();
        const created = resp?.UserCreateDate;
        if (created) {
          const d = new Date(created);
          if (!isNaN(d.getTime())) setMemberSince(d.toLocaleDateString());
        }
      } catch (e) {
        // Non-fatal; leave blank
        console.warn('Member since load failed:', e?.message || e);
      }
    };
    loadJoin();
  }, [user, userAttributes.email]);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      setUser(currentUser);
      setUserAttributes(attributes);
    } catch (error) {
      console.error('Auth error:', error);
      // Clear any local auth state and redirect
      try {
        await signOut();
      } catch (signOutError) {
        console.error('Sign out error:', signOutError);
      }
      window.location.href = '/signin';
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/home';
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const fetchContainerInfo = async () => {
    try {
      const params = {
        TableName: 'storage-management',
        FilterExpression: 'customerEmail = :email',
        ExpressionAttributeValues: {
          ':email': userAttributes.email
        }
      };
      
      const result = await dynamoDb.scan(params).promise();
      
      const items = result.Items || [];
      if (items.length > 0) {
        const sortedItems = result.Items.sort((a, b) => 
          new Date(b.lastPaymentDate) - new Date(a.lastPaymentDate)
        );
        setContainerInfo(sortedItems[0]);
        
        // Get site info
        const siteId = sortedItems[0].PK.replace('SITE#', '');
        const siteParams = {
          TableName: 'storage-management',
          Key: {
            PK: `SITE#${siteId}`,
            SK: 'METADATA'
          }
        };
        
        const siteResult = await dynamoDb.get(siteParams).promise();
        setSiteInfo(siteResult.Item);

        // Fetch site map from S3 for this site
        if (siteResult.Item?.name) {
          await fetchSiteMap(siteResult.Item.name);
        }

        // Gate: allow only if security bond is paid on the selected (latest) container
        const bondPaid = String(sortedItems[0]?.securityBondStatus || '').toLowerCase() === 'paid';
        if (!bondPaid) {
          window.location.href = '/payment';
          return;
        }
      } else {
        // No records for this user: redirect to payment to complete setup
        window.location.href = '/payment';
        return;
      }
    } catch (error) {
      console.error('Error fetching container info:', error);
      // On failure, be safe and send to payment
      window.location.href = '/payment';
    }
  };

  const fetchSiteMap = async (siteName) => {
    try {
      setSiteMapLoading(true);
      setSiteMapUrl(null);
      const slug = String(siteName).toLowerCase().trim().replace(/\s+/g, '-');
      const list = await s3.listObjectsV2({
        Bucket: 'storehere-agreements',
        Prefix: `site-maps/${slug}/`
      }).promise();
      const items = list.Contents || [];
      if (items.length > 0) {
        // use the latest object (there should be only one)
        items.sort((a, b) => new Date(b.LastModified) - new Date(a.LastModified));
        const key = items[0].Key;
        const url = await s3.getSignedUrlPromise('getObject', {
          Bucket: 'storehere-agreements',
          Key: key,
          Expires: 3600
        });
        setSiteMapUrl(url);
      } else {
        setSiteMapUrl(null);
      }
    } catch (err) {
      console.error('Error loading site map:', err);
      setSiteMapUrl(null);
    } finally {
      setSiteMapLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Container },
    { id: 'billing', name: 'Billing', icon: CreditCard },
    { id: 'documents', name: 'Documents', icon: FileText },
    { id: 'profile', name: 'Profile', icon: User },
  ];

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop/Tablet header */}
          <div className="hidden md:flex justify-between items-center py-4">
            <StoreLogo />
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-700">Welcome, {userAttributes.given_name || 'Client'}</span>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition-colors"
              >
                <LogOut size={18} />
                Sign Out
              </button>
            </div>
          </div>
          {/* Mobile header */}
          <div className="md:hidden flex justify-between items-center py-3">
            <span className="text-base font-medium text-gray-800">Welcome, {userAttributes.given_name || 'Client'}</span>
            <button
              onClick={handleSignOut}
              className="text-sm text-gray-600 hover:text-orange-500"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <div className="overflow-x-auto">
            <nav className="-mb-px flex min-w-max space-x-6 md:space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={18} />
                  {tab.name}
                </button>
              );
            })}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeTab === 'dashboard' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-6">Client Dashboard</h1>
              
              {/* Welcome Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Welcome to StoreHere, {userAttributes.given_name}!
                </h2>
                <p className="text-gray-600 mb-4">
                  Manage your storage containers and account from this dashboard.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-800">Active Containers</h3>
                    <p className="text-2xl font-bold text-orange-600">1</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-800">Monthly Payment</h3>
                    <p className="text-2xl font-bold text-blue-600">$320</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-800">Account Status</h3>
                    <p className="text-lg font-semibold text-green-600">Active</p>
                  </div>
                </div>
              </div>

              {/* Container Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Storage Container</h3>
                {containerInfo ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Container Number</label>
                      <div className="text-2xl font-bold text-gray-900">{containerInfo.number}</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Location</label>
                      <div className="text-gray-900">{siteInfo?.name || 'Loading...'}</div>
                      {siteInfo?.address && (
                        <div className="text-sm text-gray-600">{siteInfo.address}</div>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Status</label>
                      <div className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {containerInfo.status?.replace('-', ' ').toUpperCase() || 'ACTIVE'}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Next Payment Due</label>
                      <div className="text-gray-900">
                        {containerInfo.nextDueDate ? new Date(containerInfo.nextDueDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-600">Loading container information...</div>
                )}
              </div>

              {/* What to do now? */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">What to do now?</h3>
                <p className="text-gray-700">
                  Visit our office Mon–Fri 8.30am – 4.30pm to provide photo ID and receive your gate access code and container location
                </p>
              </div>

              {/* Site Map */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Site Map</h3>
                {siteMapLoading ? (
                  <div className="text-gray-600">Loading site map...</div>
                ) : siteMapUrl ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={siteMapUrl}
                      alt={`Site map for ${siteInfo?.name || 'site'}`}
                      className="max-w-full h-auto max-h-96 rounded border object-contain"
                    />
                    {siteInfo?.name && (
                      <div className="text-sm text-gray-600 mt-2">{siteInfo.name}</div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-600">Site map not available.</div>
                )}
              </div>

              {/* Support Section */}
              <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-200 p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Need Help?</h3>
                <p className="text-gray-600 mb-4">
                  If you have any questions about your storage or account, don't hesitate to reach out to our support team.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Email us at:</span>
                  <a href="mailto:storage@storehere.com.au" className="text-orange-600 hover:text-orange-700 font-medium">
                    storage@storehere.com.au
                  </a>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <BillingTab 
              userEmail={userAttributes.email} 
              userId={user?.userId || user?.username}
            />
          )}

          {activeTab === 'documents' && (
            <DocumentsTab userId={user?.userId || user?.username} />
          )}

          {activeTab === 'profile' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-6">Profile Settings</h1>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <p className="text-gray-900">{userAttributes.given_name} {userAttributes.family_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="text-gray-900">{userAttributes.email}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <p className="text-gray-900">{userAttributes.phone_number || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Details</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Account Type</label>
                        <p className="text-gray-900">{userAttributes['custom:company_name'] ? 'Business' : 'Personal'}</p>
                      </div>
                      {userAttributes['custom:company_name'] && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Company</label>
                          <p className="text-gray-900">{userAttributes['custom:company_name']}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Member Since</label>
                        <p className="text-gray-900">{memberSince || '—'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientManagement;
